import React from 'react';
import { motion } from 'framer-motion';
import { Tv, Users, Wifi, ExternalLink, Calendar, Music, Trophy, Target, Star, Award, Globe, Medal, Flame, Shield, TrendingUp } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function Gallery() {
  const {
    t
  } = useLanguage();
  const topScorers = [{
    rank: 1,
    name: 'Neymar',
    goals: 79
  }, {
    rank: 2,
    name: 'Pelé',
    goals: 77
  }, {
    rank: 3,
    name: 'Ronaldo',
    goals: 62
  }, {
    rank: 4,
    name: 'Romário',
    goals: 55
  }, {
    rank: 5,
    name: 'Zico',
    goals: 48
  }, {
    rank: 6,
    name: 'Bebeto',
    goals: 39
  }, {
    rank: 7,
    name: 'Rivaldo',
    goals: 35
  }, {
    rank: 8,
    name: 'Jairzinho',
    goals: 33
  }, {
    rank: 9,
    name: 'Ronaldinho',
    goals: 33
  }, {
    rank: 10,
    name: 'Ademir',
    goals: 32
  }];
  const records = [{
    icon: <Trophy size={24} className="text-brazil-yellow" data-id="element-93" />,
    title: 'Biggest Win',
    value: '14–0',
    subtitle: 'vs Nicaragua (1975)'
  }, {
    icon: <Star size={24} className="text-brazil-yellow" data-id="element-94" />,
    title: 'Most Capped',
    value: '142',
    subtitle: 'Cafu'
  }, {
    icon: <Award size={24} className="text-brazil-yellow" data-id="element-95" />,
    title: 'Trophy Cabinet',
    value: '18 Titles',
    subtitle: '5 WC, 9 Copa América, 4 Confed'
  }, {
    icon: <Target size={24} className="text-brazil-yellow" data-id="element-96" />,
    title: 'Golden Era',
    value: '3 Legends',
    subtitle: 'Pelé, Ronaldo, Neymar'
  }, {
    icon: <Globe size={24} className="text-brazil-yellow" data-id="element-97" />,
    title: 'FIFA Ranking',
    value: 'Top 5',
    subtitle: 'Consistently world elite'
  }, {
    icon: <Medal size={24} className="text-brazil-yellow" data-id="element-98" />,
    title: 'World Cup Best',
    value: '5x Winners',
    subtitle: '1958, 1962, 1970, 1994, 2002'
  }, {
    icon: <Flame size={24} className="text-brazil-yellow" data-id="element-99" />,
    title: 'All-Time Wins',
    value: '650+',
    subtitle: 'Since 1914'
  }, {
    icon: <Shield size={24} className="text-brazil-yellow" data-id="element-100" />,
    title: 'WC Streak',
    value: '22 Straight',
    subtitle: 'Only team to play every WC'
  }];
  const quickLinks = [{
    icon: <Tv size={18} data-id="element-101" />,
    label: t('fanHq.whereToWatch'),
    desc: t('fanHq.whereToWatchDesc'),
    link: 'https://www.foxsports.com/soccer/fifa-world-cup'
  }, {
    icon: <Users size={18} data-id="element-102" />,
    label: 'Fan Info Channel',
    desc: t('fanHq.fanZonesDesc'),
    link: 'https://www.fifa.com/en/tournaments/mens/worldcup/canadamexicousa2026/host-cities/fan-information-channels'
  }, {
    icon: <Globe size={18} data-id="element-103" />,
    label: 'FIFA Rankings',
    desc: t('fanHq.cbfAppDesc'),
    link: 'https://www.fifa.com/en/world-rankings'
  }, {
    icon: <Calendar size={18} data-id="element-104" />,
    label: t('fanHq.fullSchedule'),
    desc: t('fanHq.fullScheduleDesc'),
    link: 'https://www.espn.com/soccer/schedule/_/league/fifa.world'
  }];
  return <section className="py-12 md:py-24 bg-[#0a0a0a]" id="fan-hq" data-id="element-105">
      <div className="container mx-auto px-4 md:px-12" data-id="element-106">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16" data-id="element-107">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-brazil-green font-oswald uppercase tracking-widest text-xs md:text-sm font-bold mb-2" data-id="element-108">
            {t('fanHq.label')}
          </motion.h2>
          <motion.h3 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-3xl md:text-5xl font-oswald font-bold uppercase mb-6" data-id="element-109">
            {t('fanHq.title1')}{' '}
            <span className="text-brazil-yellow" data-id="element-110">{t('fanHq.title2')}</span>
          </motion.h3>
        </div>

        {/* Fan Essentials & Top Scorers (Side by Side) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12 md:mb-16" data-id="element-111">
          {/* Hino Nacional — Featured Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="bg-gradient-to-br from-brazil-dark-green/40 to-[#111] border border-brazil-green/20 rounded-xl p-6 md:p-8 flex flex-col" data-id="element-112">
            <div className="flex items-center gap-2 mb-1 text-brazil-yellow" data-id="element-113">
              <Music size={20} data-id="element-114" />
              <h5 className="font-oswald text-xl md:text-2xl font-bold uppercase text-white" data-id="element-115">
                Hino Nacional Brasileiro
              </h5>
            </div>
            <p className="text-xs text-brazil-green font-oswald uppercase tracking-widest mb-5" data-id="element-116">
              {t('fanHq.nationalAnthem')}
            </p>
            <div className="flex-grow" data-id="element-117">
              <p className="text-sm md:text-base text-gray-300 whitespace-pre-line italic leading-relaxed mb-4" data-id="element-118">
                "Ouviram do Ipiranga as margens plácidas{'\n'}De um povo heroico
                o brado retumbante{'\n'}E o sol da liberdade, em raios fúlgidos
                {'\n'}Brilhou no céu da pátria nesse instante."
              </p>
              <div className="pt-4 border-t border-white/10 mb-5" data-id="element-119">
                <p className="text-xs text-gray-500 whitespace-pre-line leading-relaxed" data-id="element-120">
                  They heard from the placid shores of the Ipiranga{'\n'}The
                  resounding shout of a heroic people{'\n'}And the sun of
                  liberty, in shining beams{'\n'}Shone in the sky of the
                  homeland at that instant.
                </p>
              </div>
            </div>
            <a href="https://www.youtube.com/watch?v=CZbSZ7mOCLM" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-brazil-green/20 border border-brazil-green/30 text-brazil-green hover:bg-brazil-green hover:text-white px-5 py-3 rounded-lg font-oswald uppercase text-sm font-bold tracking-wider transition-all duration-300 w-full sm:w-auto" data-id="element-121">
              <ExternalLink size={16} data-id="element-122" />
              {t('fanHq.watchYoutube')}
            </a>
          </motion.div>

          {/* All-Time Top Scorers */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="bg-[#111] border border-white/5 rounded-xl p-6 md:p-8" data-id="element-123">
            <div className="flex items-center gap-2 mb-5 text-brazil-yellow" data-id="element-124">
              <TrendingUp size={20} data-id="element-125" />
              <h5 className="font-oswald text-xl md:text-2xl font-bold uppercase text-white" data-id="element-126">
                All-Time <span className="text-brazil-green" data-id="element-127">Top Scorers</span>
              </h5>
            </div>
            <div className="space-y-2" data-id="element-128">
              {topScorers.map((scorer, i) => <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-id="element-129">
                  <div className="flex items-center gap-4" data-id="element-130">
                    <span className={`font-oswald font-bold w-6 text-center ${i < 3 ? 'text-brazil-yellow' : 'text-gray-500'}`} data-id="element-131">
                      {scorer.rank}
                    </span>
                    <span className="font-inter font-medium text-white" data-id="element-132">
                      {scorer.name}
                    </span>
                  </div>
                  <span className="font-oswald font-bold text-brazil-green" data-id="element-133">
                    {scorer.goals}{' '}
                    <span className="text-xs text-gray-500 font-normal" data-id="element-134">
                      goals
                    </span>
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Record Cards Grid */}
        <div className="mb-12 md:mb-16" data-id="element-135">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="flex items-center gap-3 mb-6" data-id="element-136">
            <Trophy className="text-brazil-yellow" size={24} data-id="element-137" />
            <h4 className="font-oswald text-2xl md:text-3xl font-bold uppercase text-white" data-id="element-138">
              National <span className="text-brazil-green" data-id="element-139">Records</span>
            </h4>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-140">
            {records.map((record, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: index * 0.05
          }} className="bg-[#111] border border-white/5 rounded-xl p-4 md:p-5 hover:border-brazil-yellow/30 transition-all group" data-id="element-141">
                <div className="mb-3 group-hover:scale-110 transition-transform origin-left" data-id="element-142">
                  {record.icon}
                </div>
                <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-wider mb-1" data-id="element-143">
                  {record.title}
                </p>
                <h5 className="font-oswald text-lg md:text-xl font-bold text-white mb-1" data-id="element-144">
                  {record.value}
                </h5>
                <p className="text-[11px] text-brazil-green leading-tight" data-id="element-145">
                  {record.subtitle}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* Quick Links Grid */}
        <div data-id="element-146">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="flex items-center gap-3 mb-6" data-id="element-147">
            <ExternalLink className="text-brazil-yellow" size={24} data-id="element-148" />
            <h4 className="font-oswald text-2xl md:text-3xl font-bold uppercase text-white" data-id="element-149">
              {t('fanHq.quickLinks')}{' '}
              <span className="text-brazil-green" data-id="element-150">
                {t('fanHq.quickLinksHighlight')}
              </span>
            </h4>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-151">
            {quickLinks.map((item, index) => <motion.a key={index} href={item.link} target="_blank" rel="noopener noreferrer" initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: index * 0.08
          }} className="bg-[#111] border border-white/5 rounded-xl p-4 md:p-5 hover:border-brazil-yellow/30 transition-all group flex flex-col min-h-[44px]" data-id="element-152">
                <div className="text-brazil-yellow mb-2 group-hover:scale-110 transition-transform origin-left" data-id="element-153">
                  {item.icon}
                </div>
                <h5 className="font-oswald text-xs md:text-sm font-bold uppercase text-white mb-1 leading-tight" data-id="element-154">
                  {item.label}
                </h5>
                <p className="text-[10px] md:text-xs text-gray-500 flex-grow" data-id="element-155">
                  {item.desc}
                </p>
                <ExternalLink size={12} className="text-gray-600 mt-2" data-id="element-156" />
              </motion.a>)}
          </div>
        </div>
      </div>
    </section>;
}